import type { ProjectCreateInput, ProjectUpdateInput } from '@plane/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ImagemPreparada } from '@/modules/projects/preparar-imagem';
import {
  createProject,
  deleteProject,
  fetchProjects,
  removeProjectImage,
  reorderProjects,
  updateProject,
  uploadProjectImage,
} from '@/modules/projects/services/projects.service';

const CHAVE = ['projects'] as const;

export function useProjects() {
  return useQuery({ queryKey: CHAVE, queryFn: fetchProjects });
}

function useInvalidar() {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries({ queryKey: CHAVE });
}

export function useCreateProject() {
  const invalidar = useInvalidar();

  return useMutation({
    mutationFn: (i: ProjectCreateInput) => createProject(i),
    onSuccess: invalidar,
  });
}

export function useUpdateProject() {
  const invalidar = useInvalidar();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ProjectUpdateInput }) =>
      updateProject(id, input),
    onSuccess: invalidar,
  });
}

export function useDeleteProject() {
  const invalidar = useInvalidar();

  return useMutation({ mutationFn: (id: string) => deleteProject(id), onSuccess: invalidar });
}

export function useReorderProjects() {
  const invalidar = useInvalidar();

  return useMutation({ mutationFn: (ids: string[]) => reorderProjects(ids), onSuccess: invalidar });
}

export function useUploadImage() {
  const invalidar = useInvalidar();

  return useMutation({
    mutationFn: ({ id, imagem, alt }: { id: string; imagem: ImagemPreparada; alt: string }) =>
      uploadProjectImage(id, imagem, alt),
    onSuccess: invalidar,
  });
}

export function useRemoveImage() {
  const invalidar = useInvalidar();

  return useMutation({
    mutationFn: ({ id, imageId }: { id: string; imageId: string }) =>
      removeProjectImage(id, imageId),
    onSuccess: invalidar,
  });
}
